import _ from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Manager } from 'socket.io-client';

const socketStatus = {
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  DISCONNECTED: 'disconnected',
  ERROR: 'error',
};

const getDefaults = () => ({
  autoReconnect: false, // Whether to automatically connect upon creation. If set to , you need to manually connect:false
  reconnection: true, // Whether reconnection is enabled or not. If set to false, you need to manually reconnect
  name: 'Generic',
  transports: ['websocket'],
  styleLog: 'background-color: rgb(74 4 78); color: white;',
});

const getAsyncDefaults = () => ({
  timeout: 10000,
});

const useSocket = (baseUrl, namespace, entryOptions) => {
  const options = useMemo(() => _.merge({}, getDefaults(), entryOptions), [entryOptions]);
  const socketRef = useRef(null);
  const [socketState, setSocketState] = useState(socketStatus.DISCONNECTED);

  const buildError = (message) => {
    const err = new Error(message);
    err.name = options.name;
    return err;
  };

  const awaitSocket = (callback, timeout) => {
    return setTimeout(() => {
      if (typeof callback === 'function') {
        callback();
      }
    }, timeout);
  };

  useEffect(() => {
    if (!socketRef.current) {
      const manager = new Manager(baseUrl);
      socketRef.current = manager.socket(namespace || '/', {
        autoConnect: options.autoReconnect,
        reconnection: options.reconnection,
        transports: options.transports,
        ...options,
      });

      socketRef.current.on('connect', () => {
        console.info(
          `%c[Socket ${options?.name} | connect]:\nUrl: ${baseUrl}\nNamespace: ${namespace}\nID: ${socketRef?.current.id}`,
          options.styleLog
        );
        if (socketRef.current.connected && socketRef?.current.id !== undefined) {
          setSocketState(socketStatus.CONNECTED);
        }
      });
      socketRef.current.on('connect_error', (message) => {
        setSocketState(socketStatus.ERROR);
        console.error(
          `[Socket ${options?.name} | connect_error]:\n${JSON.stringify(message, null, 2)}`
        );
        setTimeout(() => {
          if (options.autoReconnect) {
            // socketRef.current.connect();
          }
        }, 1300);
      });

      socketRef.current.on('disconnect', () => {
        setSocketState(socketStatus.DISCONNECTED);
        console.error(`[Socket ${options?.name} | disconnect]: \nID: ${socketRef?.current.id}`);
      });
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.off();
        socketRef.current.disconnect();
      }
      socketRef.current = null;
      setSocketState(socketStatus.DISCONNECTED);
    };
  }, [baseUrl, namespace, options.autoReconnect, options.name]);

  const listenEvent = useCallback((...props) => {
    if (!socketRef.current) return;
    socketRef.current.on(...props);
  }, []);

  const emitEvent = useCallback((...props) => {
    if (!socketRef.current) return;
    socketRef.current.emit(...props);
  }, []);

  const emitEventWithAck = useCallback(async (channel, payload, time) => {
    if (!socketRef.current) throw new Error(`Socket is not available`);
    return socketRef.current.timeout(time).emitWithAck(channel, payload);
  }, []);

  const closeEvent = useCallback((channel) => {
    if (!socketRef.current) return;
    socketRef.current.off(channel);
  }, []);

  const closeAll = useCallback(() => {
    if (!socketRef.current) return;
    socketRef.current.off();
  }, []);

  const tryConnect = useCallback(() => {
    if (!socketRef.current) return;
    socketRef.current.connect();
  }, []);

  const tryDisconnect = useCallback(() => {
    if (!socketRef.current) return;
    socketRef.current.disconnect();
  }, []);

  const asyncEvent = async (event, body, entryOpts) => {
    const opts = _.merge({}, getAsyncDefaults(), entryOpts);
    const errors = [];
    if (typeof event !== 'string' || event.trim().length === 0)
      errors.push('Event must be a valid string');
    if (typeof body !== 'object') errors.push('Body must be a valid message to send');
    if (typeof opts.timeout !== 'number') errors.push('Timeout must be a number');
    if (opts.timeout < 100) errors.push('Timeout must be a positive integer greater than 100');
    if (errors.length > 0) throw new Error(errors.join(', '));

    return new Promise((resolve, reject) => {
      const removeListener = null;

      // ----- Iniciando contador desborde de timer
      const overflow = awaitSocket(() => {
        if (typeof removeListener !== 'function') return;
        closeEvent(event);
        reject(new Error('TIMEOUT'));
      }, opts.timeout);

      listenEvent(event, (payload) => {
        clearTimeout(overflow);
        closeEvent(payload);
        resolve(payload);
      });

      emitEvent(event, body);
    });
  };

  const setToken = useCallback((token) => {
    if (!socketRef.current) return;
    if (token !== undefined) {
      socketRef.current.disconnect();
      socketRef.current.auth = { token };
      setTimeout(() => {
        socketRef?.current?.connect();
      }, 1000);
    }
  }, []);

  const updateQuery = (query) => {
    if (!socketRef.current) throw new Error('Socket is not available');
    return new Promise((res, rej) => {
      if (query !== undefined && typeof query === 'object') {
        socketRef.current.disconnect();
        socketRef.current.io.opts.query = query;
        setTimeout(() => {
          socketRef?.current?.connect();
          const isEqual = _.isEqual(socketRef.current?.io?.opts?.query, query);
          if (isEqual) {
            console.debug(
              `%c[Socket ${options?.name} | Query updated]: \n${JSON.stringify(query, null, 2)}`,
              options.styleLog
            );
            res(true);
          } else {
            rej(buildError('An error occurred while updating query'));
          }
        }, 300);
      }
    });
  };

  const updateUrl = (newUrl) => {
    if (!socketRef.current) throw new Error('Socket is not available');
    return new Promise((res, rej) => {
      if (newUrl !== undefined && newUrl !== '') {
        socketRef.current.disconnect();
        socketRef.current.io.uri = newUrl;
        setTimeout(() => {
          socketRef?.current?.connect();
          if (socketRef.current?.io?.uri === newUrl) {
            console.debug(`%c[Socket ${options?.name} | Url updated]: ${newUrl}`, options.styleLog);
            res(true);
          } else {
            rej(buildError('An error occurred while updating url'));
          }
        }, 300);
      }
    });
  };

  return {
    socketRef,
    listenEvent,
    emitEvent,
    emitEventWithAck,
    asyncEvent,
    socketState,
    closeEvent,
    closeAll,
    socketStatus,
    tryConnect,
    tryDisconnect,
    setToken,
    updateQuery,
    updateUrl,
  };
};

export default useSocket;
